import { isDevMode } from '@angular/core'
import { CompleteHocuspocusProviderConfiguration, ConstructableOutgoingMessage, HocuspocusProvider } from '@hocuspocus/provider'
import { createDecoder, readVarString, readVarUint } from 'lib0/decoding'
import { createEncoder, toUint8Array, writeVarString, writeVarUint, writeVarUint8Array } from 'lib0/encoding'
import { encodeStateVector } from 'yjs'
import { environment } from '../../../environments/environment'

const MESSAGE_TYPE_STATELESS = 5

const MESSAGE_TYPE_NAMES: Record<number, string> = {
  0: 'Sync',
  1: 'Awareness',
  2: 'Auth',
  3: 'QueryAwareness',
  4: 'SyncReply',
  [MESSAGE_TYPE_STATELESS]: 'Stateless',
  6: 'BroadcastStateless',
  7: 'CLOSE',
  8: 'SyncStatus',
}

function describeMessage(bytes: Uint8Array): string {
  try {
    const decoder = createDecoder(bytes)
    const documentName = readVarString(decoder)
    const messageType = readVarUint(decoder)
    const typeName = MESSAGE_TYPE_NAMES[messageType] ?? `Unknown(${messageType})`
    return `type=${typeName}, document=${documentName}, size=${bytes.byteLength}B`
  } catch {
    return `size=${bytes.byteLength}B (failed to decode)`
  }
}

class SseSyncProviderLogger {
  private enabled = isDevMode()

  debug(message: string) {
    if (this.enabled) {
      console.debug(`[SseSyncProvider] ${message}`)
    }
  }

  warn(message: string) {
    console.warn(`[SseSyncProvider] ${message}`)
  }

  error(message: string) {
    console.error(`[SseSyncProvider] ${message}`)
  }

  messageReceived(bytes: Uint8Array) {
    if (this.enabled) {
      console.debug(`[SseSyncProvider] Message received: ${describeMessage(bytes)}`)
    }
  }
}

const logger = new SseSyncProviderLogger()

function toUrlSafeBase64(value: string): string {
  return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function bytesToBase64(bytes: Uint8Array): string {
  const chunkSize = 8192
  const parts: string[] = []
  for (let offset = 0; offset < bytes.byteLength; offset += chunkSize) {
    const chunk = bytes.subarray(offset, Math.min(offset + chunkSize, bytes.byteLength))
    parts.push(String.fromCharCode(...chunk))
  }
  return btoa(parts.join(''))
}

function base64ToBytes(base64: string): Uint8Array | null {
  try {
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }
    return bytes
  } catch {
    return null
  }
}

const FLUSH_INTERVAL_MS = 150

function encodeOutgoingMessage(Message: ConstructableOutgoingMessage, args: any = {}): Uint8Array {
  const message = new (Message as any)()
  return toUint8Array(message.get(args))
}

const STATELESS_SYNC_COMPLETE = 'sync-complete'

function sendMessages(sessionId: string, encodedMessages: string[], transport: 'sse' | 'poll'): Promise<Response> {
  return fetch(`/api/tiptap-editor/session/${sessionId}/message`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages: encodedMessages, transport }),
    credentials: 'include',
  })
}

type PollResponse = {
  messages: string[]
  statelessPerOriginSeqMark: Record<string, number>
}

function pollDocument(
  encodedDocumentName: string,
  sessionId: string,
  stateVector: string,
  statelessPerOriginSeqMark: Record<string, number>,
  signal: AbortSignal
): Promise<Response> {
  return fetch(`/api/tiptap-editor/document/${encodedDocumentName}/poll`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, stateVector, statelessPerOriginSeqMark }),
    credentials: 'include',
    signal,
  })
}

function reportPollingFallback(encodedDocumentName: string): Promise<Response> {
  return fetch(`/api/tiptap-editor/document/${encodedDocumentName}/polling-fallback`, {
    method: 'POST',
    credentials: 'include',
  })
}

function postHeartbeatPong(sessionId: string, payload: string): Promise<Response> {
  return fetch(`/api/tiptap-editor/session/${sessionId}/heartbeat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ payload }),
    credentials: 'include',
  })
}

type BufferedOutboundMessage = {
  message: ConstructableOutgoingMessage
  args: any
}

export type SseSyncProviderConfiguration = Pick<CompleteHocuspocusProviderConfiguration, 'name' | 'document' | 'onSynced'> & {
  onStatelessPayload?: (payload: string) => void
  /**
   * Skip SSE entirely and start in polling mode. Set for users already known to
   * be unable to hold an SSE connection, so they don't wait out the sync
   * deadline on every page load.
   */
  forcePolling?: boolean
}

const HEARTBEAT_TIMEOUT_MS = 15_000
const HEARTBEAT_CHECK_INTERVAL_MS = 5_000

const RECONNECT_BASE_DELAY_MS = 1_000
const RECONNECT_MAX_DELAY_MS = 30_000
const MAX_RECONNECT_ATTEMPTS = 10

const SYNC_DEADLINE_MS = environment.tiptapSseInitialSyncDeadlineMs
const POLL_INTERVAL_MS = environment.tiptapPollIntervalMs
const MAX_POLL_INTERVAL_MS = 2_000
const HIDDEN_POLL_INTERVAL_MS = 15_000
const MAX_POLL_BACKOFF_LEVEL = 4
const POLL_REQUEST_TIMEOUT_MS = 10_000
const MAX_FALLBACK_REPORT_RETRIES = 5
const FALLBACK_REPORT_RETRY_DELAY_MS = 5_000

function isDocumentHidden(): boolean {
  return typeof globalThis.document !== 'undefined' && globalThis.document.hidden
}

function mergePerOriginSeqMark(current: Record<string, number>, incoming: Record<string, number>): Record<string, number> {
  const merged = { ...current }
  for (const [originId, seq] of Object.entries(incoming)) {
    merged[originId] = Math.max(merged[originId] ?? 0, seq)
  }
  return merged
}

function isRetryableStatus(status: number): boolean {
  return status === 429 || status >= 500
}

export class SseSyncProvider extends HocuspocusProvider {
  private eventSource: EventSource | null = null

  private connectionId: string | null = null

  private isDestroyed = false

  private isReconnecting = false
  /**
   * Used to buffer messages that get sent before we establish a connection with
   * the backend so we don't lose any messages.
   */
  private outboundBuffer: Array<BufferedOutboundMessage> = []
  /**
   * Used to buffer messages periodically so we can batch multiple messages
   * within the `FLUSH_INTERVAL_MS` period for sending in a single request.
   */
  private pendingMessages: string[] = []

  private flushTimer: ReturnType<typeof setTimeout> | null = null

  private lastHeartbeatTimestamp = 0

  private heartbeatCheckInterval: ReturnType<typeof setInterval> | null = null

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null

  private reconnectAttempts = 0

  private ackedConnectionId: string | null = null

  private pollSessionId: string | null = null

  private get isPolling(): boolean {
    return this.pollSessionId !== null
  }

  private syncDeadlineTimer: ReturnType<typeof setTimeout> | null = null

  private pollTimer: ReturnType<typeof setTimeout> | null = null

  private pollBackoffLevel = 0

  private pollInFlight = false

  private onVisibilityChange: (() => void) | null = null

  private statelessPerOriginSeqMark: Record<string, number> = {}

  private fallbackReported = false

  private fallbackReportInFlight = false

  private fallbackReportAttempts = 0

  private fallbackReportTimer: ReturnType<typeof setTimeout> | null = null

  constructor(configuration: SseSyncProviderConfiguration) {
    super({
      ...configuration,
      // Proxy all calls to the websocket provider as we're not using it for
      // syncing, but the HocuspocusProvider expects it to be there and will at
      // times make calls to it that we don't need. Passing a proxy allows us to
      // avoid having to rewrite the underlying provider whilst ensuring that
      // any calls to the websocket provider are effectively no-ops.
      websocketProvider: new Proxy({} as any, {
        get: () => () => {},
      }),
    })

    this.on('synced', () => {
      this.clearSyncDeadline()
      this.acknowledgeInitialSync()
    })

    if (configuration.forcePolling === true) {
      logger.warn('User is a known polling fallback user, skipping SSE.')
      this.startPolling()
      return
    }

    this.connectSse()
    this.startSyncDeadline()
  }

  private startSyncDeadline() {
    this.clearSyncDeadline()
    this.syncDeadlineTimer = setTimeout(() => {
      this.syncDeadlineTimer = null
      if (this.isDestroyed || this.synced || this.isPolling) {
        return
      }
      logger.warn(`Initial sync not completed within ${SYNC_DEADLINE_MS}ms, falling back to polling.`)
      this.startPolling()
    }, SYNC_DEADLINE_MS)
  }

  private clearSyncDeadline() {
    if (this.syncDeadlineTimer !== null) {
      clearTimeout(this.syncDeadlineTimer)
      this.syncDeadlineTimer = null
    }
  }

  private acknowledgeInitialSync() {
    if (this.isDestroyed || !this.synced || this.connectionId === null || this.connectionId === this.ackedConnectionId) {
      return
    }
    this.ackedConnectionId = this.connectionId
    this.sendStateless(STATELESS_SYNC_COMPLETE)
  }

  private connectSse() {
    if (this.eventSource !== null) {
      this.eventSource.close()
    }

    this.eventSource = new EventSource(`/api/tiptap-editor/document/${toUrlSafeBase64(this.configuration.name)}/sse`, {
      withCredentials: true,
    })

    this.eventSource.addEventListener('connectionId', (event: MessageEvent<string>) => {
      if (this.isDestroyed) {
        return
      }
      this.connectionId = event.data
      this.isReconnecting = false
      this.reconnectAttempts = 0
      this.clearReconnectTimer()
      this.lastHeartbeatTimestamp = Date.now()
      logger.debug(`Connection established (sessionId: ${event.data})`)
      this.startHeartbeatMonitoring()
      this.enqueueSyncStep1()
      this.flushOutboundBuffer()
      this.acknowledgeInitialSync()
    })

    this.eventSource.addEventListener('message', (event: MessageEvent<string>) => {
      if (this.isDestroyed) {
        return
      }
      const bytes = base64ToBytes(event.data)
      if (bytes === null) {
        logger.error(`Discarding SSE message with invalid base64 payload (length=${event.data.length}).`)
        return
      }
      logger.messageReceived(bytes)
      this.handlePossibleStatelessMessage(bytes)
      this.onMessage({
        ...event,
        data: bytes.buffer as ArrayBuffer,
        target: null as any,
      })
    })

    this.eventSource.addEventListener('heartbeat', (event: MessageEvent<string>) => {
      logger.debug('Message received: type=Heartbeat')
      this.lastHeartbeatTimestamp = Date.now()
      this.sendHeartbeatPong(event.data)
    })

    this.eventSource.addEventListener('error', () => {
      if (this.isDestroyed) {
        return
      }
      this.reconnectSse()
    })
  }

  private reconnectSse() {
    if (this.isDestroyed || this.isPolling || this.reconnectTimer !== null) {
      return
    }

    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      logger.warn(`SSE reconnection failed ${this.reconnectAttempts} times, falling back to polling.`)
      this.startPolling()
      return
    }

    this.isReconnecting = true
    this.connectionId = null
    this.clearFlushTimer()
    this.stopHeartbeatMonitoring()
    if (this.eventSource !== null) {
      this.eventSource.close()
      this.eventSource = null
    }

    const delay = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** this.reconnectAttempts, RECONNECT_MAX_DELAY_MS)
    this.reconnectAttempts++
    logger.debug(`Disconnected from EventSource, will reconnect in ${delay}ms`)
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      if (this.isDestroyed || this.isPolling) {
        return
      }
      this.connectSse()
    }, delay)
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  private closeSse() {
    this.clearReconnectTimer()
    this.stopHeartbeatMonitoring()
    if (this.eventSource !== null) {
      this.eventSource.close()
      this.eventSource = null
    }
    this.connectionId = null
  }

  private startPolling() {
    if (this.isDestroyed || this.isPolling) {
      return
    }
    this.pollSessionId = crypto.randomUUID()
    this.closeSse()
    this.reportFallback()
    this.flushOutboundBuffer()
    this.startVisibilityWatch()
    void this.poll().finally(() => this.scheduleNextPoll())
    logger.warn(`Polling mode started (sessionId: ${this.pollSessionId}).`)
  }

  private reportFallback() {
    if (this.fallbackReported || this.fallbackReportInFlight) {
      return
    }
    this.fallbackReportInFlight = true
    reportPollingFallback(toUrlSafeBase64(this.configuration.name))
      .then((response) => {
        this.fallbackReportInFlight = false
        if (response.ok) {
          this.fallbackReported = true
          return
        }
        this.scheduleFallbackReportRetry(`status ${response.status}`)
      })
      .catch((error) => {
        this.fallbackReportInFlight = false
        this.scheduleFallbackReportRetry(String(error))
      })
  }

  private scheduleFallbackReportRetry(reason: string) {
    if (this.isDestroyed || !this.isPolling || this.fallbackReportAttempts >= MAX_FALLBACK_REPORT_RETRIES) {
      logger.warn(`Giving up reporting polling fallback after ${this.fallbackReportAttempts} retries (${reason}).`)
      return
    }
    this.fallbackReportAttempts += 1
    logger.warn(`Failed to report polling fallback (${reason}); retry ${this.fallbackReportAttempts}/${MAX_FALLBACK_REPORT_RETRIES}.`)
    this.fallbackReportTimer = setTimeout(() => {
      this.fallbackReportTimer = null
      this.reportFallback()
    }, FALLBACK_REPORT_RETRY_DELAY_MS)
  }

  private clearFallbackReportRetry() {
    if (this.fallbackReportTimer !== null) {
      clearTimeout(this.fallbackReportTimer)
      this.fallbackReportTimer = null
    }
  }

  private scheduleNextPoll() {
    if (this.isDestroyed || !this.isPolling) {
      return
    }
    if (this.pollTimer !== null) {
      clearTimeout(this.pollTimer)
    }
    this.pollTimer = setTimeout(() => {
      this.pollTimer = null
      void this.poll().finally(() => this.scheduleNextPoll())
    }, this.nextPollDelay())
  }

  private nextPollDelay(): number {
    if (isDocumentHidden()) {
      return HIDDEN_POLL_INTERVAL_MS
    }
    return Math.min(POLL_INTERVAL_MS * 2 ** this.pollBackoffLevel, MAX_POLL_INTERVAL_MS)
  }

  private startVisibilityWatch() {
    if (typeof globalThis.document === 'undefined' || this.onVisibilityChange !== null) {
      return
    }
    this.onVisibilityChange = () => {
      if (this.isDestroyed || !this.isPolling || isDocumentHidden()) {
        return
      }
      this.pollBackoffLevel = 0
      if (this.pollTimer !== null) {
        clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
      void this.poll().finally(() => this.scheduleNextPoll())
    }
    globalThis.document.addEventListener('visibilitychange', this.onVisibilityChange)
  }

  private stopVisibilityWatch() {
    if (this.onVisibilityChange !== null && typeof globalThis.document !== 'undefined') {
      globalThis.document.removeEventListener('visibilitychange', this.onVisibilityChange)
    }
    this.onVisibilityChange = null
  }

  private stopPolling() {
    this.pollSessionId = null
    this.stopVisibilityWatch()
    this.clearFallbackReportRetry()
    if (this.pollTimer !== null) {
      clearTimeout(this.pollTimer)
      this.pollTimer = null
    }
  }

  private async poll(): Promise<void> {
    if (this.isDestroyed || this.pollSessionId === null || this.pollInFlight) {
      return
    }
    this.pollInFlight = true
    try {
      await this.runPoll(this.pollSessionId)
    } finally {
      this.pollInFlight = false
    }
  }

  private incrementPollBackoff() {
    this.pollBackoffLevel = Math.min(this.pollBackoffLevel + 1, MAX_POLL_BACKOFF_LEVEL)
  }

  private async runPoll(pollSessionId: string): Promise<void> {
    const stateVector = bytesToBase64(encodeStateVector(this.document))
    const controller = new AbortController()
    const timeoutHandle = setTimeout(() => controller.abort(), POLL_REQUEST_TIMEOUT_MS)

    let response: Response
    try {
      response = await pollDocument(toUrlSafeBase64(this.configuration.name), pollSessionId, stateVector, this.statelessPerOriginSeqMark, controller.signal)
    } catch {
      this.incrementPollBackoff()
      return
    } finally {
      clearTimeout(timeoutHandle)
    }

    if (!response.ok) {
      logger.warn(`Poll request failed with status ${response.status}.`)
      this.incrementPollBackoff()
      if (!isRetryableStatus(response.status)) {
        logger.error(`Poll returned non-retryable status ${response.status}; stopping polling.`)
        this.stopPolling()
      }
      return
    }

    let body: PollResponse
    try {
      body = (await response.json()) as PollResponse
    } catch {
      this.incrementPollBackoff()
      return
    }

    if (this.isDestroyed || !this.isPolling) {
      return
    }

    this.applyPolledMessages(body.messages)
    this.statelessPerOriginSeqMark = mergePerOriginSeqMark(this.statelessPerOriginSeqMark, body.statelessPerOriginSeqMark ?? {})

    if (body.messages.length > 0) {
      this.pollBackoffLevel = 0
    } else {
      this.incrementPollBackoff()
    }

    if (!this.synced) {
      this.synced = true
    }
  }

  private applyPolledMessages(messages: string[]) {
    for (const encoded of messages) {
      const bytes = base64ToBytes(encoded)
      if (bytes === null) {
        logger.error(`Discarding polled message with invalid base64 payload (length=${encoded.length}).`)
        continue
      }
      logger.messageReceived(bytes)
      this.handlePossibleStatelessMessage(bytes)
      this.onMessage({ data: bytes.buffer as ArrayBuffer, target: null } as unknown as MessageEvent)
    }
  }

  override destroy() {
    logger.debug('Disconnecting from EventSource, provider destroyed')
    this.isDestroyed = true
    this.stopPolling()
    this.outboundBuffer = []
    this.pendingMessages = []
    this.clearFlushTimer()
    this.clearReconnectTimer()
    this.clearSyncDeadline()
    this.stopHeartbeatMonitoring()
    if (this.eventSource !== null) {
      this.eventSource.close()
      this.eventSource = null
    }
    super.destroy()
  }

  override attach() {
    // no-op: Use SSE instead.
  }

  override detach() {
    // no-op: Use SSE instead.
  }

  override async send(message: ConstructableOutgoingMessage, args: any): Promise<void> {
    if (this.isDestroyed) {
      return
    }

    if (this.isPolling) {
      this.pollBackoffLevel = 0
      this.pendingMessages.push(bytesToBase64(encodeOutgoingMessage(message, args)))
      this.schedulePendingMessagesFlush()
      return
    }

    if (this.connectionId === null || this.isReconnecting) {
      this.outboundBuffer.push({ message, args })
      return
    }

    this.pendingMessages.push(bytesToBase64(encodeOutgoingMessage(message, args)))
    this.schedulePendingMessagesFlush()
  }

  private sendHeartbeatPong(payload: string) {
    if (this.isDestroyed || this.connectionId === null || payload.length === 0) {
      return
    }
    void postHeartbeatPong(this.connectionId, payload).catch(() => {})
  }

  private startHeartbeatMonitoring() {
    this.stopHeartbeatMonitoring()
    this.heartbeatCheckInterval = setInterval(() => {
      if (Date.now() - this.lastHeartbeatTimestamp > HEARTBEAT_TIMEOUT_MS) {
        logger.warn('Heartbeat timeout, reconnecting SSE')
        this.reconnectSse()
      }
    }, HEARTBEAT_CHECK_INTERVAL_MS)
  }

  private stopHeartbeatMonitoring() {
    if (this.heartbeatCheckInterval !== null) {
      clearInterval(this.heartbeatCheckInterval)
      this.heartbeatCheckInterval = null
    }
  }

  private clearFlushTimer() {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer)
      this.flushTimer = null
    }
  }

  private schedulePendingMessagesFlush() {
    if (this.flushTimer !== null) {
      return
    }

    this.flushTimer = setTimeout(() => {
      this.flushTimer = null
      this.flushPendingMessages()
    }, FLUSH_INTERVAL_MS)
  }

  private requeueOutbound(failedMessages: string[]) {
    this.pendingMessages.unshift(...failedMessages)
    if (this.isPolling) {
      this.schedulePendingMessagesFlush()
    } else {
      this.reconnectSse()
    }
  }

  private async flushPendingMessages(): Promise<void> {
    const sessionId = this.isPolling ? this.pollSessionId : this.connectionId
    if (this.isDestroyed || sessionId === null || this.pendingMessages.length === 0) {
      return
    }

    const messages = this.pendingMessages
    this.pendingMessages = []

    let response: Response
    try {
      response = await sendMessages(sessionId, messages, this.isPolling ? 'poll' : 'sse')
    } catch {
      this.requeueOutbound(messages)
      return
    }

    if (!response.ok && isRetryableStatus(response.status)) {
      this.requeueOutbound(messages)
    }
  }

  private flushOutboundBuffer() {
    const messages = this.outboundBuffer
    this.outboundBuffer = []

    for (const bufferedMessage of messages) {
      this.send(bufferedMessage.message, bufferedMessage.args)
    }
  }

  private handlePossibleStatelessMessage(bytes: Uint8Array) {
    const onStatelessPayload = (this.configuration as SseSyncProviderConfiguration).onStatelessPayload
    if (onStatelessPayload === undefined) {
      return
    }

    try {
      const decoder = createDecoder(bytes)
      readVarString(decoder) // skip document name
      const messageType = readVarUint(decoder)
      if (messageType === MESSAGE_TYPE_STATELESS) {
        const payload = readVarString(decoder)
        logger.debug(`Stateless message received: payload=${payload}`)
        onStatelessPayload(payload)
      }
    } catch (error) {
      logger.warn(`Failed to parse possible stateless message: ${error}`)
    }
  }

  private enqueueSyncStep1() {
    if (this.isDestroyed || this.connectionId === null) {
      return
    }

    const encoder = createEncoder()
    writeVarString(encoder, this.configuration.name)
    writeVarUint(encoder, 0) // MessageType.Sync
    writeVarUint(encoder, 0) // messageYjsSyncStep1
    writeVarUint8Array(encoder, encodeStateVector(this.document))

    this.pendingMessages.push(bytesToBase64(toUint8Array(encoder)))
    this.schedulePendingMessagesFlush()
  }
}
